import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  Box,
  Badge,
  Select,
} from '@chakra-ui/react';
import { Info, Warning } from 'phosphor-react';
import {
  ILegalizacaoCreate,
  TipoLegalizacao,
  TIPO_LEGALIZACAO_LABEL,
} from '../../../../hooks/useLegalizacao';
import {
  camposChaveAnteriores,
  cnpjValido,
  dataEmDias,
  dataInputParaIso,
  rotuloComRegistry,
} from '../legalizacaoPlaybook';
import { JUNTA_POR_UF, resolverRegistry, UFS_ATENDIDAS, ufExigeMunicipio } from '../registries';
import { CnpjLookupButton } from '../../contratos/criar/components/CnpjLookupButton';

// Extras do "Iniciar aqui" (híbrido): a coluna-alvo, as etapas assumidas (feitas fora
// do sistema) e os campos-chave (subtype) informados p/ as etapas seguintes usarem.
export interface IniciarAquiExtra {
  colunaAlvo: string;
  assumidas: string[];
  camposChave: Record<string, string>;
}

interface LegalizacaoCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  tipo: TipoLegalizacao;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (payload: ILegalizacaoCreate, extra?: IniciarAquiExtra) => void;
  isSubmitting?: boolean;
  /** "Iniciar aqui": coluna-alvo do processo (undefined/null/1ª coluna = criação normal). */
  colunaAlvo?: string | null;
  /** Colunas do board do tipo — pra derivar as etapas assumidas + campos-chave. */
  colunas?: string[];
}

// Constituição CRIA a empresa → só a razão social pretendida (a empresa ainda não
// existe; sócios/briefing entram no Levantamento + wizard). Alteração/Baixa agem
// sobre uma empresa EXISTENTE → informa o CNPJ e consulta a razão social (base
// pública, não exige cadastro em tb_empresa). O card nasce na 1ª coluna do tipo,
// salvo "Iniciar aqui" (colunaAlvo), que cria e salta pra etapa-alvo.
export const LegalizacaoCreateModal: React.FC<LegalizacaoCreateModalProps> = ({
  isOpen,
  onClose,
  tipo,
  onSubmit,
  isSubmitting = false,
  colunaAlvo,
  colunas = [],
}) => {
  const isConstituicao = tipo === 'constituicao';
  const [razaoSocial, setRazaoSocial] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [juntaUf, setJuntaUf] = useState('GO');
  // DERIVADA, não estado próprio: sigla que se guarda separado é sigla que diverge da
  // UF na primeira troca esquecida.
  const juntaSigla = JUNTA_POR_UF[juntaUf] ?? '';
  const [municipio, setMunicipio] = useState('');
  const [camposChave, setCamposChave] = useState<Record<string, string>>({});
  // Prazo no formato do <input type="date"> ('' = não definido). Os atalhos só
  // PREENCHEM este campo — uma fonte da verdade só, senão atalho e data divergem.
  const [prazo, setPrazo] = useState('');

  // "Iniciar aqui": etapas anteriores viram assumidas + coletamos só os campos-chave.
  const alvoIdx = colunaAlvo ? colunas.indexOf(colunaAlvo) : -1;
  const iniciarAqui = alvoIdx > 0;
  const assumidas = useMemo(
    () => (iniciarAqui ? colunas.slice(0, alvoIdx) : []),
    [iniciarAqui, colunas, alvoIdx],
  );
  const chaves = useMemo(
    () => (iniciarAqui ? camposChaveAnteriores(tipo, colunas, alvoIdx) : []),
    [iniciarAqui, tipo, colunas, alvoIdx],
  );

  // Reseta os defaults sempre que o modal reabre.
  useEffect(() => {
    if (isOpen) {
      setRazaoSocial('');
      setCnpj('');
      setJuntaUf('GO');
      setMunicipio('');
      setCamposChave({});
      // Nada pré-selecionado de propósito: o prazo varia demais entre processos pra ter
      // um default honesto, e default aceito sem pensar produz data que ninguém honra.
      setPrazo('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    const payload: ILegalizacaoCreate = {
      tipo_legalizacao: tipo,
      razao_social: razaoSocial.trim() || null,
      // ⚠️ ALFANUMÉRICO, não `\D`. O gate acima é `cnpjValido`, que aceita o CNPJ
      // alfanumérico — é a razão de ele existir. Um strip de não-dígitos aqui aprovaria
      // 14 caracteres e gravaria 9, calado: o processo nasceria com o CNPJ mutilado e a
      // busca do quadro nunca mais acharia a empresa.
      cnpj: isConstituicao ? null : cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase() || null,
      empresa_id: null,
      junta_sigla: juntaSigla.trim() || null,
      junta_uf: juntaUf.trim().toUpperCase() || null,
      municipio: municipio.trim() || null,
      expira_em: prazo ? dataInputParaIso(prazo) : null,
    };
    if (iniciarAqui && colunaAlvo) {
      // Só manda os campos-chave efetivamente preenchidos.
      const preenchidos: Record<string, string> = {};
      Object.entries(camposChave).forEach(([k, v]) => {
        if (v?.trim()) preenchidos[k] = v.trim();
      });
      onSubmit(payload, { colunaAlvo, assumidas, camposChave: preenchidos });
    } else {
      onSubmit(payload);
    }
  };

  // O gate antigo era `!!cnpj.replace(/\D/g,'')` — QUALQUER caractere passava, e dois
  // dígitos criavam processo. Agora exige CNPJ válido de verdade (dígitos verificadores).
  const cnpjOk = isConstituicao || cnpjValido(cnpj);
  const cnpjPreenchido = !!cnpj.replace(/[^0-9A-Za-z]/g, '');
  /**
   * Município vira OBRIGATÓRIO nas UFs que distinguem capital × interior (SP, RJ, BA).
   *
   * ⚠️ Não é rigor cadastral: nessas UFs o município decide se um passo OBRIGATÓRIO
   * aparece — CPL no Rio, TVL da SUCOM em Salvador. `resolverRegistry` compara o texto
   * com o nome da capital, então município em branco resolve calado para INTERIOR, que é
   * o registry SEM o passo. Uma empresa de Salvador criada sem município perderia o TVL e
   * ninguém veria. Nas demais UFs segue opcional, porque lá ele não muda fluxo nenhum.
   */
  // Resolvido com o município corrente: em UF com split o registry muda conforme ele, e o
  // aviso de genérico precisa refletir o que o processo vai receber de fato.
  const registryDaUf = resolverRegistry(juntaUf, municipio);
  const municipioOk = !ufExigeMunicipio(juntaUf) || !!municipio.trim();
  const podeConfirmar = !!razaoSocial.trim() && cnpjOk && municipioOk;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl" bg="white">
        <ModalHeader>
          {iniciarAqui ? `Iniciar em «${colunaAlvo}»` : 'Novo processo de legalização'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box mb={4}>
            <Text fontSize="11px" fontWeight="700" color="gray.500" mb={1}>
              Tipo
            </Text>
            <Badge colorScheme="purple" fontSize="10px" px={2} py={1} borderRadius="md">
              {TIPO_LEGALIZACAO_LABEL[tipo]}
            </Badge>
          </Box>

          {/* Alteração/Baixa: CNPJ da empresa existente + consulta pública. */}
          {!isConstituicao && (
            <FormControl mb={4}>
              <FormLabel fontSize="11px" fontWeight="700" color="gray.700">
                CNPJ da empresa
              </FormLabel>
              <InputGroup>
                <Input
                  value={cnpj}
                  onChange={e => setCnpj(e.target.value)}
                  placeholder="00.000.000/0000-00"
                  borderRadius="lg"
                />
                <InputRightElement width="auto" pr={1}>
                  <CnpjLookupButton
                    getCnpj={() => cnpj}
                    onFound={dados => {
                      setRazaoSocial(dados.razao_social ?? '');
                      {
                        // Normaliza e só aceita UF conhecida: agora o campo é um
                        // <Select>, e valor fora da lista deixaria o seletor em branco
                        // — pior que manter o anterior, porque some sem avisar.
                        const uf = (dados.junta_uf ?? '').trim().toUpperCase();
                        if (JUNTA_POR_UF[uf]) setJuntaUf(uf);
                      }
                      if (dados.sede?.cidade) setMunicipio(dados.sede.cidade);
                    }}
                  />
                </InputRightElement>
              </InputGroup>
              {/* Aviso só depois de digitar algo: campo intocado não é erro. */}
              {cnpjPreenchido && !cnpjOk ? (
                <Text fontSize="11px" color="#C22B2B" mt={1} fontWeight="500">
                  CNPJ inválido — confira os dígitos.
                </Text>
              ) : (
                <Text fontSize="11px" color="gray.400" mt={1}>
                  Consulta a razão social na base pública — não precisa estar cadastrada.
                </Text>
              )}
            </FormControl>
          )}

          <FormControl mb={4}>
            <FormLabel fontSize="11px" fontWeight="700" color="gray.700">
              {isConstituicao ? 'Razão social pretendida' : 'Razão social'}
            </FormLabel>
            <Input
              value={razaoSocial}
              onChange={e => setRazaoSocial(e.target.value)}
              placeholder={
                isConstituicao ? 'ex.: Bar do Zé LTDA' : 'preenchida pela consulta do CNPJ'
              }
              borderRadius="lg"
            />
          </FormControl>

          {/* UF ESCOLHIDA, JUNTA DERIVADA.
              Eram dois campos de texto livre independentes (defaults JUCEG e GO):
              bastava trocar a UF e esquecer a sigla pra gravar combinação impossível —
              e havia um `JUCEG · MG` no banco. Existe uma junta por estado, então a
              sigla não é um dado a digitar, é uma consequência da UF. */}
          <FormControl>
            <FormLabel fontSize="11px" fontWeight="700" color="gray.700">
              UF da junta
            </FormLabel>
            <Flex align="center" gap={3}>
              <Select
                value={juntaUf}
                onChange={e => setJuntaUf(e.target.value)}
                borderRadius="lg"
                maxW="120px"
              >
                {UFS_ATENDIDAS.map(uf => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </Select>
              <Flex align="baseline" gap={1.5} minW={0}>
                <Text
                  fontSize="10px"
                  fontWeight="700"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                >
                  Junta
                </Text>
                <Text fontSize="13px" fontWeight="700" color="gray.700" noOfLines={1}>
                  {juntaSigla || '—'}
                </Text>
              </Flex>
            </Flex>
            {/* AVISO DE FLUXO GENÉRICO, no ponto da ESCOLHA.
                O drawer também avisa, mas lá é tarde: o operador já criou o processo e só
                descobre ao abrir o card. Aqui ele decide sabendo. Não é bloqueio — a UF
                continua selecionável; o fluxo genérico funciona, só não foi levantado
                nesta junta. */}
            {registryDaUf?.generico && (
              <Flex align="flex-start" gap={2} mt={2}>
                <Box mt="2px" flexShrink={0}>
                  <Warning size={13} weight="fill" color="var(--chakra-colors-orange-500)" />
                </Box>
                <Text fontSize="11px" color="#B4750B">
                  Fluxo <b>genérico</b> para {juntaUf}: o roteiro segue o padrão nacional (REDESIM),
                  mas as exigências desta junta não foram levantadas. Confira na {juntaSigla} antes
                  de protocolar.
                </Text>
              </Flex>
            )}
          </FormControl>

          {/* Município da sede: SÓ input faltava (state + payload já existiam). Sem ele, a
              constituição não alcança capital×interior (Balcão Único/Facilita SP/CPL). Em
              alt/baixa vem pré-preenchido pela consulta do CNPJ (editável). */}
          <FormControl mt={4}>
            <FormLabel fontSize="11px" fontWeight="700" color="gray.700">
              Município (cidade da sede)
              {ufExigeMunicipio(juntaUf) && (
                <Text as="span" color="#C22B2B" ml={1}>
                  *
                </Text>
              )}
            </FormLabel>
            <Input
              value={municipio}
              onChange={e => setMunicipio(e.target.value)}
              placeholder="ex.: Goiânia, São Paulo, Rio de Janeiro"
              borderRadius="lg"
            />
            {/* O texto muda com a UF porque o EFEITO muda: em SP/RJ/BA o município decide
                se um passo obrigatório aparece; nas demais ele é só cadastro. O texto fixo
                anterior prometia "define capital × interior" em 11 UFs onde não define nada. */}
            <Text fontSize="11px" color={ufExigeMunicipio(juntaUf) ? '#B4750B' : 'gray.400'} mt={1}>
              {ufExigeMunicipio(juntaUf)
                ? `Obrigatório em ${juntaUf}: a junta trata capital e interior de formas diferentes, e o município é o que decide qual fluxo vale.`
                : 'Cidade da sede da empresa.'}
            </Text>
          </FormControl>

          {/* PRAZO DE ENTREGA — o campo que faltava.
              Até aqui o card nascia sempre com `expira_em` nulo e o prazo só podia ser
              posto depois, abrindo o card no bloco "Prazo (SLA)" do drawer. Ninguém
              fazia: 8 de 9 processos estavam sem prazo, e o termômetro de risco do
              quadro classificava quase nada.

              Atalhos + data livre, NADA pré-selecionado: um default aceito sem pensar
              vira prazo que ninguém honra, e aí o semáforo fica verde mentindo — pior
              que o buraco atual, porque ninguém confere verde. Os atalhos existem só
              pra escolher custar um clique em vez de navegar num calendário. */}
          <FormControl mt={4}>
            <FormLabel fontSize="11px" fontWeight="700" color="gray.700">
              Prazo de entrega
            </FormLabel>
            <Flex gap={2} wrap="wrap" align="center">
              {[15, 30, 45].map(d => {
                const alvo = dataEmDias(d);
                const ativo = prazo === alvo;
                return (
                  <Box
                    key={d}
                    as="button"
                    type="button"
                    aria-pressed={ativo}
                    onClick={() => setPrazo(ativo ? '' : alvo)}
                    px="12px"
                    h="34px"
                    borderRadius="9px"
                    borderWidth="1.5px"
                    borderStyle="solid"
                    borderColor={ativo ? '#7C3AED' : 'rgba(15,23,42,0.1)'}
                    bg={ativo ? 'rgba(124,58,237,0.09)' : 'transparent'}
                    color={ativo ? '#6D28D9' : '#64748B'}
                    fontSize="13px"
                    fontWeight="700"
                    transition="all .14s"
                    _hover={{ borderColor: '#7C3AED' }}
                  >
                    {d} dias
                  </Box>
                );
              })}
              <Input
                type="date"
                value={prazo}
                onChange={e => setPrazo(e.target.value)}
                borderRadius="lg"
                size="sm"
                fontSize="13px"
                maxW="170px"
                h="34px"
              />
            </Flex>
            {/* A data resultante por extenso: "30 dias" é abstrato, "26/09" é o
                compromisso. Quem cobra olha a data, não a duração. */}
            {prazo ? (
              <Text fontSize="11px" color="gray.600" mt={1.5}>
                Vence em{' '}
                <Text as="span" fontWeight="700" color="gray.700">
                  {new Date(dataInputParaIso(prazo)).toLocaleDateString('pt-BR', {
                    weekday: 'long',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })}
                </Text>
                .
              </Text>
            ) : (
              <Text fontSize="11px" color="#B4750B" mt={1.5}>
                Sem prazo o processo fica fora do termômetro de risco — não entra na conta de
                atrasados nem acende alerta. Dá pra definir depois no card.
              </Text>
            )}
          </FormControl>

          {/* "Iniciar aqui": aviso + campos-chave das etapas assumidas (opcionais). */}
          {iniciarAqui ? (
            <Box mt={5}>
              <Flex
                bg="orange.50"
                borderWidth="1px"
                borderColor="orange.100"
                borderRadius="lg"
                p={3}
                gap={2.5}
                align="flex-start"
              >
                <Box color="orange.500" flexShrink={0} mt="1px">
                  <Info size={16} weight="duotone" />
                </Box>
                <Text fontSize="13px" color="orange.800" lineHeight="1.5">
                  As <b>{assumidas.length}</b> etapa(s) anterior(es) serão marcadas como{' '}
                  <b>assumidas</b> (feitas fora do sistema): {assumidas.join(' · ')}. Se já tiver os
                  dados-chave em mãos, informe abaixo — senão pode deixar em branco e preencher
                  depois.
                </Text>
              </Flex>

              {chaves.length > 0 && (
                <Stack spacing={3} mt={4}>
                  {chaves.map(c => (
                    <FormControl key={c.key}>
                      <FormLabel fontSize="11px" fontWeight="700" color="gray.700" mb={1}>
                        {rotuloComRegistry(c, juntaUf, municipio)}
                      </FormLabel>
                      <Input
                        value={camposChave[c.key] ?? ''}
                        onChange={e =>
                          setCamposChave(prev => ({ ...prev, [c.key]: e.target.value }))
                        }
                        placeholder={c.placeholder}
                        type={c.tipo === 'date' || c.tipo === 'number' ? c.tipo : 'text'}
                        borderRadius="lg"
                        size="sm"
                        fontSize="13px"
                      />
                    </FormControl>
                  ))}
                </Stack>
              )}
            </Box>
          ) : (
            <Text fontSize="11px" color="gray.400" mt={4}>
              {isConstituicao
                ? 'A empresa será constituída neste processo — os sócios e o briefing (imóvel/regime) entram na etapa de Levantamento.'
                : 'O processo age sobre a empresa existente informada pelo CNPJ.'}
            </Text>
          )}
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" borderRadius="full" onClick={onClose} isDisabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            colorScheme="purple"
            borderRadius="full"
            px={5}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={!podeConfirmar}
          >
            {iniciarAqui ? 'Criar nesta etapa' : 'Criar processo'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LegalizacaoCreateModal;
